import { Box, ListView } from '@razorpay/blade/components';
import React, { ReactElement, useState } from 'react';

import api from 'api';
import { FormProvider } from 'forms';
import { useFilters } from 'hooks/useFilters';
import { usePagination } from 'hooks/usePagination';
import { useQuery } from 'react-query';
import SalaryStructureListView from './SalaryStructureListView';
import Filters from './components/Filters';
import { salaryStructureQueries, statusFiltersMap } from './constants';
import {
  SalaryStructureCreationFlow,
  SalaryStructureFormData,
  StructureCreateType,
} from './creation';
import { filterSalaryStructures } from './utils';
import { useListSalaryStructures } from './hooks';

const SalaryStructureView = (): ReactElement => {
  const [creationFlow, setCreationFlow] = useState<{
    isOpen: boolean;
    creationType: StructureCreateType | null;
  }>({
    isOpen: false,
    creationType: null,
  });
  // Pagination state
  const { setCurrentPage } = usePagination({
    page: 0,
    limit: 10,
  });

  // Filters state
  const { currentFilters } = useFilters({
    filtersMap: {
      status: 'status',
      search: 'search',
    },
    onParamsChange(params) {
      setCurrentPage(0);
    },
  });

  // Data fetching with react-query
  const { data, isFetching, refetch, error } = useListSalaryStructures({
    enabled: true,
    queryKey: [salaryStructureQueries.getSalaryStructuresList],
  });
  const structures = data ?? [];

  const hasCreatedSalaryStructureBefore = structures.length > 1;

  const handleCreateClick = (creationType: StructureCreateType) => {
    // Open creation flow
    setCreationFlow({
      isOpen: true,
      creationType,
    });
  };

  return (
    <Box marginTop="spacing.5">
      <ListView>
        <Filters structures={structures} />

        <Box marginTop="spacing.7">
          <SalaryStructureListView
            structures={filterSalaryStructures(structures, currentFilters)}
            isFetching={isFetching}
            onRetry={refetch}
            error={error}
            onCreateStructure={handleCreateClick}
          />
        </Box>
      </ListView>
      {creationFlow.creationType ? (
        <FormProvider
          initialState={
            {
              creationType: creationFlow.creationType,
              hasCreatedSalaryStructureBefore: hasCreatedSalaryStructureBefore,
            } as SalaryStructureFormData
          }>
          <SalaryStructureCreationFlow
            isOpen={creationFlow.isOpen}
            onClose={() => setCreationFlow({ isOpen: false, creationType: null })}
          />
        </FormProvider>
      ) : null}
    </Box>
  );
};

export default SalaryStructureView;
